import React, { useCallback, useEffect, useState } from 'react';
import { Shield, KeyRound, Smartphone } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { api, mfaApi, type MfaDevice } from '../../../lib/api';
import { useAuth } from '../../../components/auth/use-auth';
import { QRCodeSVG } from 'qrcode.react';
import McpZugaengeCard from '../../../components/auth/McpZugaengeCard';

type MfaSetupPayload = {
  setup: {
    secret: string;
    otpauthUrl: string;
    issuer: string;
    accountName: string;
  };
  status: {
    enabled: boolean;
    totpEnabled: boolean;
    emailOtpEnabled: boolean;
    hasSecret: boolean;
  };
  required: boolean;
};

const AccountSecurity = () => {
  const { loadMfaSetup, enableMfa, disableMfa } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordChangedInfo, setPasswordChangedInfo] = useState<string | null>(null);

  const [mfaSetup, setMfaSetup] = useState<MfaSetupPayload | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [isLoadingMfa, setIsLoadingMfa] = useState(false);
  const [isSavingMfa, setIsSavingMfa] = useState(false);

  const [devices, setDevices] = useState<MfaDevice[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);

  const loadDevices = useCallback(async () => {
    setIsLoadingDevices(true);
    try {
      const rows = await mfaApi.getMyDevices();
      setDevices(rows);
    } catch {
      toast.error('Geräte konnten nicht geladen werden');
    } finally {
      setIsLoadingDevices(false);
    }
  }, []);

  const refreshMfaState = useCallback(async () => {
    setIsLoadingMfa(true);
    try {
      const data = (await loadMfaSetup()) as MfaSetupPayload;
      setMfaSetup(data);
    } catch {
      toast.error('MFA-Status konnte nicht geladen werden');
    } finally {
      setIsLoadingMfa(false);
    }
  }, [loadMfaSetup]);

  useEffect(() => {
    void refreshMfaState();
    void loadDevices();
  }, [refreshMfaState, loadDevices]);

  const submitPasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Bitte alle Passwort-Felder ausfüllen');
      return;
    }
    setPasswordChangedInfo(null);
    if (newPassword !== confirmPassword) {
      toast.error('Neues Passwort und Bestätigung stimmen nicht überein');
      return;
    }
    setIsSavingPassword(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Passwort erfolgreich geändert');
      setPasswordChangedInfo('Andere aktive Geräte/Sitzungen wurden automatisch abgemeldet.');
      await loadDevices();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Passwort konnte nicht geändert werden';
      toast.error(msg);
    } finally {
      setIsSavingPassword(false);
    }
  };

  const startMfaSetup = async () => {
    await refreshMfaState();
  };

  const submitEnableMfa = async () => {
    if (!mfaCode) {
      toast.error('Bitte den TOTP-Code eingeben');
      return;
    }
    setIsSavingMfa(true);
    try {
      await enableMfa(mfaCode);
      setMfaCode('');
      await refreshMfaState();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'MFA konnte nicht aktiviert werden';
      toast.error(msg);
    } finally {
      setIsSavingMfa(false);
    }
  };

  const submitDisableMfa = async () => {
    if (!disableCode) {
      toast.error('Bitte den aktuellen TOTP-Code eingeben');
      return;
    }
    const shouldDisable = window.confirm(
      mfaSetup?.required
        ? 'MFA ist für Ihre Rolle Pflicht. Nach dem Deaktivieren werden Sie in Kürze abgemeldet und müssen MFA bei der nächsten Anmeldung neu einrichten. Fortfahren?'
        : 'MFA wirklich deaktivieren? Danach ist Ihr Konto nur noch mit Passwort geschützt.',
    );
    if (!shouldDisable) {
      return;
    }

    setIsSavingMfa(true);
    try {
      await disableMfa(disableCode);
      setDisableCode('');
      await refreshMfaState();
      await loadDevices();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'MFA konnte nicht deaktiviert werden';
      toast.error(msg);
    } finally {
      setIsSavingMfa(false);
    }
  };

  const getDeviceRef = (device: MfaDevice): string => {
    if (device.device_id) return `device:${device.device_id}`;
    return `token:${device.id}`;
  };

  const getDeviceLabel = (device: MfaDevice): string => {
    if (device.device_name && device.device_name.trim() !== '') return device.device_name;
    if (device.user_agent && device.user_agent.trim() !== '') return 'Browser';
    return 'Unbekanntes Gerät';
  };

  const revokeDevice = async (device: MfaDevice) => {
    const deviceRef = getDeviceRef(device);
    try {
      await mfaApi.revokeMyDevice(deviceRef);
      setDevices((prev) => prev.filter((d) => d.id !== device.id));
      toast.success('Gerät entfernt');
    } catch {
      toast.error('Gerät konnte nicht entfernt werden');
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Mein Konto & Sicherheit</h1>
        <p className="text-muted-foreground">Passwort ändern, MFA aktivieren und angemeldete Geräte verwalten.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Passwort ändern</CardTitle>
          <CardDescription>Ändern Sie Ihr eigenes Login-Passwort.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="current-password">Aktuelles Passwort</Label>
            <Input id="current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="new-password">Neues Passwort</Label>
            <Input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="confirm-password">Neues Passwort bestätigen</Label>
            <Input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </div>
          <div className="flex justify-end">
            <Button onClick={submitPasswordChange} disabled={isSavingPassword}>{isSavingPassword ? 'Speichern…' : 'Passwort speichern'}</Button>
          </div>
          {passwordChangedInfo ? (
            <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {passwordChangedInfo}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> Multi-Faktor-Authentifizierung</CardTitle>
          <CardDescription>Aktivieren oder deaktivieren Sie TOTP-basierte MFA für Ihr Konto.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingMfa ? (
            <p className="text-sm text-muted-foreground">Lade MFA-Status…</p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Badge variant={mfaSetup?.status?.enabled ? 'info' : 'secondary'}>
                  {mfaSetup?.status?.enabled ? 'MFA aktiv' : 'MFA inaktiv'}
                </Badge>
                {mfaSetup?.required ? <Badge variant="destructive">Pflicht</Badge> : null}
              </div>

              {!mfaSetup?.status?.enabled && (
                <div className="space-y-3 rounded border p-3">
                  <Button variant="outline" onClick={startMfaSetup}>Setup-Daten laden/aktualisieren</Button>
                  <div className="space-y-1">
                    {mfaSetup?.setup.otpauthUrl ? (
                      <div className="inline-block rounded-md border bg-white p-2">
                        <QRCodeSVG value={mfaSetup.setup.otpauthUrl} size={168} />
                      </div>
                    ) : null}
                    <p className="text-sm text-muted-foreground break-all">Secret: <span className="font-mono">{mfaSetup?.setup.secret || '—'}</span></p>
                    <p className="text-xs text-muted-foreground break-all">OTPAuth URL: <span className="font-mono">{mfaSetup?.setup.otpauthUrl || '—'}</span></p>
                  </div>
                  <div>
                    <Label htmlFor="mfa-code">TOTP-Code</Label>
                    <Input id="mfa-code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="6-stelliger Code" />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={submitEnableMfa} disabled={isSavingMfa}>{isSavingMfa ? 'Aktivieren…' : 'MFA aktivieren'}</Button>
                  </div>
                </div>
              )}

              {mfaSetup?.status?.enabled && (
                <div className="space-y-3 rounded border p-3">
                  <div>
                    <Label htmlFor="mfa-disable-code">TOTP-Code zur Bestätigung</Label>
                    <Input
                      id="mfa-disable-code"
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value)}
                      placeholder="6-stelliger Code aus der Authenticator-App"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button variant="destructive" onClick={submitDisableMfa} disabled={isSavingMfa || !disableCode}>
                      {isSavingMfa ? 'Deaktivieren…' : 'MFA deaktivieren'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5" /> Meine Geräte</CardTitle>
          <CardDescription>Aktive Sitzungen dieses Kontos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoadingDevices ? (
            <p className="text-sm text-muted-foreground">Lade Geräte…</p>
          ) : devices.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine aktiven Geräte.</p>
          ) : (
            devices.map((d) => (
              <div key={`${d.id}-${d.device_id ?? 'n/a'}`} className="rounded border p-3 flex items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-sm font-medium">{getDeviceLabel(d)}</p>
                  <p className="text-xs text-muted-foreground break-all">{d.user_agent || '—'}</p>
                  <p className="text-xs text-muted-foreground">IP: {d.ip_address || '—'} · Letzte Aktivität: {d.last_used_at || d.created_at}</p>
                </div>
                <Button variant="destructive" size="sm" onClick={() => revokeDevice(d)}>
                  Entfernen
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <McpZugaengeCard />
    </div>
  );
};

export default AccountSecurity;
